// Delarna i formulären för en plats (b5): typ, tidszon, position och navets nyckel.
//
// Positionen anges utan extern tjänst: med webbläsarens position när man är på plats, med lat
// och lon för hand, eller med en länk från en karta som koordinaterna läses ur.

import { arKortlank, type Navsort, type Platstyp, tolkaPosition } from "@batkoll/gemensam";
import { useMemo, useState } from "preact/hooks";
import { Kort } from "../komponenter.tsx";

export const TYPVAL: { typ: Platstyp; namn: string }[] = [
  { typ: "hus", namn: "Hus" },
  { typ: "fritidshus", namn: "Fritidshus" },
  { typ: "bat", namn: "Båt" },
  { typ: "husbil", namn: "Husbil" },
  { typ: "annat", namn: "Annat" },
];

export const NAVVAL: Record<Navsort, string> = {
  ombord: "Navet ombord",
  hus: "Nobø-hubben, med navet i huset (till exempel en Raspberry Pi)",
  ha: "Home Assistant, med navet som app i Home Assistant",
};

/** Webbläsarens tidszon, till exempel Europe/Stockholm. */
export function webblasarensTidszon(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Stockholm";
  } catch {
    return "Europe/Stockholm";
  }
}

const VANLIGA = [
  "Europe/Stockholm",
  "Europe/Oslo",
  "Europe/Copenhagen",
  "Europe/Helsinki",
  "Europe/Madrid",
  "Europe/Berlin",
  "Europe/London",
  "Atlantic/Canary",
];

/** Tidszonerna som webbläsaren känner till, eller de vanligaste. */
function tidszoner(): string[] {
  const alla = (
    Intl as unknown as { supportedValuesOf?: (nyckel: string) => string[] }
  ).supportedValuesOf?.("timeZone");
  return alla && alla.length > 0 ? alla : VANLIGA;
}

/** Söktexten som den jämförs med tidszonerna: små bokstäver, och understreck för mellanslag. */
const soknyckel = (s: string) => s.trim().toLowerCase().replace(/\s+/g, "_");

/**
 * Tidszonen (b6): ett val bland tidszonerna som webbläsaren känner till, med en sökruta. Den går
 * inte att skriva fritt. Ger sökningen en enda träff väljs den.
 */
export function Tidszonsval(props: { varde: string; andra: (v: string) => void }) {
  const [sok, setSok] = useState("");
  const alla = useMemo(() => {
    const lista = tidszoner();
    const extra = [webblasarensTidszon()].filter((z) => !lista.includes(z));
    return [...lista, ...extra].sort();
  }, []);
  const nyckel = soknyckel(sok);
  const traffar = nyckel ? alla.filter((z) => z.toLowerCase().includes(nyckel)) : alla;
  // Det valda står alltid med, så att valet visar det också när sökningen inte träffar det.
  const lista = traffar.includes(props.varde) ? traffar : [props.varde, ...traffar];
  return (
    <>
      <label>
        Sök tidszon
        <input
          type="search"
          autoComplete="off"
          placeholder="Till exempel madrid eller oslo"
          value={sok}
          onInput={(e) => {
            const v = e.currentTarget.value;
            setSok(v);
            const k = soknyckel(v);
            const t = k ? alla.filter((z) => z.toLowerCase().includes(k)) : [];
            if (t.length === 1 && t[0]) props.andra(t[0]);
          }}
        />
      </label>
      <label>
        Tidszon
        <select value={props.varde} onChange={(e) => props.andra(e.currentTarget.value)}>
          {lista.map((z) => (
            <option key={z} value={z}>
              {z.replaceAll("_", " ")}
            </option>
          ))}
        </select>
      </label>
      {nyckel && traffar.length === 0 && (
        <p class="liten">Ingen tidszon matchar sökningen. Sök på en stad, till exempel Madrid.</p>
      )}
    </>
  );
}

export interface Positionsvarde {
  position: { lat: number; lon: number } | null;
  /** Falskt när lat eller lon inte är giltiga grader. */
  giltig: boolean;
}

const lasTal = (s: string) => Number(s.trim().replace(",", "."));

/** Positionen: "Använd min position", lat och lon för hand, eller en kartlänk. */
export function Positionsval(props: {
  start: { lat: number; lon: number } | null;
  andra: (v: Positionsvarde) => void;
}) {
  const [lat, setLat] = useState(props.start ? String(props.start.lat) : "");
  const [lon, setLon] = useState(props.start ? String(props.start.lon) : "");
  const [lank, setLank] = useState("");
  const [besked, setBesked] = useState<string | null>(null);
  const [fel, setFel] = useState<string | null>(null);
  const [soker, setSoker] = useState(false);
  const kanSoka = typeof navigator !== "undefined" && "geolocation" in navigator;

  function satt(nyLat: string, nyLon: string) {
    setLat(nyLat);
    setLon(nyLon);
    if (!nyLat.trim() && !nyLon.trim()) {
      setFel(null);
      props.andra({ position: null, giltig: true });
      return;
    }
    const p = { lat: lasTal(nyLat), lon: lasTal(nyLon) };
    const giltig =
      Number.isFinite(p.lat) &&
      Number.isFinite(p.lon) &&
      Math.abs(p.lat) <= 90 &&
      Math.abs(p.lon) <= 180;
    setFel(giltig ? null : "Lat ska vara mellan −90 och 90, och lon mellan −180 och 180.");
    props.andra({ position: giltig ? p : null, giltig });
  }

  function minPosition() {
    setBesked(null);
    setFel(null);
    setSoker(true);
    navigator.geolocation.getCurrentPosition(
      (g) => {
        setSoker(false);
        const r = (v: number) => String(Math.round(v * 100_000) / 100_000);
        satt(r(g.coords.latitude), r(g.coords.longitude));
        setBesked("Positionen är hämtad från den här enheten.");
      },
      () => {
        setSoker(false);
        setFel("Positionen gick inte att hämta. Tillåt platstjänster, eller skriv lat och lon.");
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 },
    );
  }

  function klistra(text: string) {
    setLank(text);
    setBesked(null);
    if (!text.trim()) return;
    const p = tolkaPosition(text);
    if (p) {
      satt(String(p.lat), String(p.lon));
      setBesked(`Läste ${p.lat}, ${p.lon} ur länken.`);
    } else if (arKortlank(text)) {
      setFel(
        "En kortlänk går inte att läsa utan att fråga kartan. Öppna länken och kopiera adressen ur webbläsaren, eller skriv lat och lon.",
      );
    } else {
      setFel("Hittar inga koordinater i texten. Skriv lat och lon, till exempel 61.315, 12.264.");
    }
  }

  return (
    <fieldset class="positionsval">
      <legend>Position (får saknas)</legend>
      {kanSoka && (
        <button type="button" class="knapp-andra" disabled={soker} onClick={minPosition}>
          {soker ? "Hämtar positionen …" : "Använd min position"}
        </button>
      )}
      <div class="tva">
        <label>
          Lat
          <input
            inputMode="decimal"
            autoComplete="off"
            placeholder="61.315"
            value={lat}
            onInput={(e) => satt(e.currentTarget.value, lon)}
          />
        </label>
        <label>
          Lon
          <input
            inputMode="decimal"
            autoComplete="off"
            placeholder="12.264"
            value={lon}
            onInput={(e) => satt(lat, e.currentTarget.value)}
          />
        </label>
      </div>
      <label>
        Eller klistra in en kartlänk
        <input
          autoComplete="off"
          placeholder="https://www.google.com/maps/…"
          value={lank}
          onInput={(e) => klistra(e.currentTarget.value)}
        />
      </label>
      <p class="liten">
        Positionen ger väderprognosen för förvärmningen. Två decimaler räcker, ungefär en kilometer.
        Ingen karttjänst frågas.
      </p>
      {(lat || lon) && (
        <button
          type="button"
          class="knapp-lank"
          onClick={() => {
            setLank("");
            setBesked(null);
            satt("", "");
          }}
        >
          Ta bort positionen
        </button>
      )}
      {besked && (
        <p class="ok-text" role="status">
          {besked}
        </p>
      )}
      {fel && (
        <p class="fel" role="alert">
          {fel}
        </p>
      )}
    </fieldset>
  );
}

const VAR_NYCKELN: Record<Navsort, string> = {
  ombord: "Lägg in den i navet ombord, som NAV_NYCKEL.",
  hus: "På Raspberry Pi:n: kör sudo batkoll-nav nyckel och klistra in den. Den visas inte där heller.",
  ha: "I Home Assistant: Inställningar → Appar → Båtkoll nav → Konfiguration, i fältet nav_nyckel. Spara och starta om appen.",
};

/** Navets nyckel, som visas en gång, med en knapp för att kopiera den. */
export function Nyckelruta(props: { nyckel: string; sort: Navsort; klar: () => void }) {
  const [kopierad, setKopierad] = useState<boolean | null>(null);

  async function kopiera() {
    try {
      await navigator.clipboard.writeText(props.nyckel);
      setKopierad(true);
    } catch {
      setKopierad(false);
    }
  }

  return (
    <Kort rubrik="Navets nyckel" bred>
      <p class="meddelande viktigt">
        Nyckeln visas bara nu, och går inte att visa igen. Kopiera den och lägg in den i navet.
      </p>
      <textarea
        class="nyckel"
        readOnly
        rows={2}
        spellcheck={false}
        aria-label="Navets nyckel"
        value={props.nyckel}
        onFocus={(e) => e.currentTarget.select()}
      />
      <div class="knapprad">
        <button type="button" class="knapp-huvud" onClick={kopiera}>
          Kopiera nyckeln
        </button>
        <button type="button" class="knapp-andra" onClick={props.klar}>
          Jag har sparat nyckeln
        </button>
      </div>
      {kopierad === true && (
        <p class="ok-text" role="status">
          Nyckeln är kopierad.
        </p>
      )}
      {kopierad === false && (
        <p class="fel" role="alert">
          Det gick inte att kopiera. Markera nyckeln och kopiera den själv.
        </p>
      )}
      <p class="liten">{VAR_NYCKELN[props.sort]}</p>
      <p class="liten">
        Tappar du bort den: ta fram en ny under platsens inställningar. Då slutar den här att gälla.
      </p>
    </Kort>
  );
}
